import type { ClassValue } from 'clsx'
import type { z, ZodSchema } from 'zod'

import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import type { TProfileResponse } from '@/api/auth/types'
import type { AlamatType } from '@/api/references/types'
import type { Option } from '@/types/common'
import type { TMetaDataType } from '@/types/response'

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs))
}

export function delay(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve()
    }, ms)
  })
}

/**
 * Masks a plain-text NIK, NIP, or No. KTP for display.
 * Keeps the first 4 and last 4 characters; the middle becomes *.
 * Example: 3504123456780003 → 3504********0003
 * Encrypted or empty values render as '-'.
 */
export function maskIdentityNumber(value?: string | null): string {
  if (value == null) return '-'

  const raw = String(value).trim()
  if (!raw || raw === '-') return '-'

  const compact = raw.replace(/[\s.-]/g, '')
  const maskedMiddle = (length: number) => '*'.repeat(length)

  if (/^[\dA-Za-z]{4}[X*]+[\dA-Za-z]{4}$/.test(compact)) {
    return `${compact.slice(0, 4)}${maskedMiddle(compact.length - 8)}${compact.slice(-4)}`
  }

  const isPlainText = /^[A-Z0-9]+$/i.test(compact) && compact.length <= 24
  if (!isPlainText) return '-'
  if (compact.length <= 8) return compact

  return `${compact.slice(0, 4)}${maskedMiddle(compact.length - 8)}${compact.slice(-4)}`
}

export const formatNumber = (val: number) => {
  if (typeof val === 'number' && !Number.isNaN(val)) {
    const stringValue = val.toString()
    const decimalIndex = stringValue.indexOf('.')

    if (decimalIndex !== -1) {
      const decimalPart = stringValue.substring(decimalIndex + 1)

      const truncatedDecimalPart = decimalPart.substring(0, 2)

      const formattedValue = `${stringValue.substring(0, decimalIndex)}.${truncatedDecimalPart}`

      return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    } else {
      return stringValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
  }
  return val
}

export const getWhatsAppLink = (phone: string | null) => {
  if (!phone) return null
  const cleanPhone = phone.replace(/\D/g, '')
  const formattedPhone = cleanPhone.startsWith('0')
    ? `62${cleanPhone.substring(1)}`
    : cleanPhone.startsWith('62')
      ? cleanPhone
      : `62${cleanPhone}`
  return `https://wa.me/${formattedPhone}`
}

export const calculateAge = (birthDate: string | null): number => {
  if (!birthDate) return 0
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

export const formatAddress = (alamat: AlamatType): string => {
  if (!alamat) return '-'

  const parts = [
    alamat.alamat,
    alamat.desa?.nama ? `Desa ${alamat.desa.nama}` : null,
    alamat.kecamatan?.nama ? `Kec. ${alamat.kecamatan.nama}` : null,
    alamat.kota?.nama ? `Kab. ${alamat.kota.nama}` : null,
    alamat.provinsi?.nama ? alamat.provinsi.nama.toUpperCase() : null
  ].filter(Boolean)

  return parts.join(', ') || '-'
}

export function toOptionFormat(region?: { nama?: string; id?: string }) {
  return {
    label: region?.nama || '',
    value: region?.id || ''
  }
}

export function getPaginationRange(meta?: TMetaDataType) {
  if (!meta) return ''
  const { total_data, current_page, per_page } = meta
  const totalData = total_data ?? 0
  const from = totalData === 0 ? 0 : (current_page - 1) * per_page + 1
  const to = totalData === 0 ? 0 : Math.min(current_page * per_page, totalData)
  return `Showing ${from} to ${to} of ${totalData} entries`
}

export function formatStringFromUnderscore(
  str: string,
  uppercase = false
): string {
  const formatted = str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  return uppercase ? formatted.toUpperCase() : formatted
}

export function uriToJson<T extends ZodSchema<any>>({
  uri,
  schema
}: {
  uri?: string
  schema: T
}) {
  if (!uri) return undefined

  const decoded = JSON.parse(decodeURIComponent(uri))
  const parsed = schema.safeParse(decoded)

  if (!parsed.success) return undefined

  return parsed.data as z.infer<T>
}

export function wilayahFromJson(json: string) {
  try {
    const parsed = JSON.parse(json)
    return {
      id: parsed.id || '',
      kode: parsed.kode || '',
      nama: parsed.nama || ''
    }
  } catch (error) {
    console.error('Invalid JSON format:', error)
    return { id: '', kode: '', nama: '' }
  }
}

export const serializeOption = (
  val: Option | null | undefined
): string | undefined => {
  if (!val) return undefined
  return encodeURIComponent(JSON.stringify(val))
}

export const parseOption = (val: string | undefined): Option | undefined => {
  if (!val) return undefined
  return JSON.parse(decodeURIComponent(val))
}

export const getTabAndFilter = (firstHeader: string) => {
  switch (firstHeader) {
    case 'Provinsi':
      return { tab: 'province', filterKey: 'province' }
    case 'Kabupaten/Kota':
      return { tab: 'city', filterKey: 'city' }
    case 'Kecamatan':
      return { tab: 'district', filterKey: 'district' }
    case 'Desa':
      return { tab: 'village', filterKey: 'village' }
    default:
      return { tab: 'province', filterKey: 'province' }
  }
}

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return ''

  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  } catch {
    return ''
  }
}

export const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-'

  try {
    const date = new Date(dateString)
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return '-'
  }
}

export const getStatusInfo = (profile: TProfileResponse) => {
  const now = new Date()
  const expiringDate = profile.expiring_on
    ? new Date(profile.expiring_on)
    : null

  if (profile.status === 'inactive') {
    return { status: 'Tidak Aktif', color: 'text-red-600', duration: '' }
  }

  if (expiringDate && expiringDate < now) {
    return {
      status: 'Kadaluarsa',
      color: 'text-orange-600',
      duration: `Berakhir: ${formatDate(profile.expiring_on)}`
    }
  }

  if (profile.status === 'active') {
    const duration = expiringDate
      ? `Berakhir: ${formatDate(profile.expiring_on)}`
      : ''
    return { status: 'Aktif', color: 'text-green-600', duration }
  }

  return { status: 'Tidak Diketahui', color: 'text-gray-600', duration: '' }
}

export function downloadResponseXlsx(response: any, fileName: string) {
  const blob = new Blob([response], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()

  setTimeout(() => {
    URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }, 100)
}

export function isLahanInDesa(
  lahan: any,
  desaId?: string | null,
  desaName?: string | null
): boolean {
  if (!lahan) return false
  if (!desaId && !desaName) return true

  const lahanDesaId =
    lahan.alamat?.desa?.id ??
    lahan.desa?.id ??
    lahan.id_desa ??
    lahan.desa_id ??
    (typeof lahan.alamat?.desa === 'string' ? lahan.alamat.desa : undefined)
  const lahanDesaName =
    lahan.alamat?.desa?.nama ??
    lahan.desa?.nama ??
    (typeof lahan.alamat?.desa === 'string' ? lahan.alamat.desa : undefined)

  if (desaId && lahanDesaId) {
    if (
      String(lahanDesaId).trim().toLowerCase() ===
      String(desaId).trim().toLowerCase()
    ) {
      return true
    }
  }

  if (desaName && lahanDesaName) {
    if (
      String(lahanDesaName).trim().toLowerCase() ===
      String(desaName).trim().toLowerCase()
    ) {
      return true
    }
  }

  return false
}
